import { Box, Button, ButtonGroup, Stack } from '@mui/material'
import { useAtom, useAtomValue } from 'jotai'
import { useEffect, useMemo } from 'react'
import {
  gatheringSidebarModeAtom,
  selectedGatheringAreaAtom,
  selectedGatheringAtom,
  selectedGatheringItemAtom,
} from '@/atoms'
import { allRecords } from '@/data/gatheringIndex'
import { SKILL_TIERS } from '@/types/constants'

export function GatheringSidebarPanel() {
  const [mode, setMode] = useAtom(gatheringSidebarModeAtom)
  const [selectedArea, setSelectedArea] = useAtom(selectedGatheringAreaAtom)
  const [selectedItem, setSelectedItem] = useAtom(selectedGatheringItemAtom)
  const gathering = useAtomValue(selectedGatheringAtom)
  const isFishing = gathering === 'fishing'

  const typeRecords = useMemo(
    () => allRecords.filter((r) => r.gatheringType === gathering),
    [gathering]
  )

  const areas = useMemo(() => {
    const areaNames = new Set(typeRecords.map((r) => r.area))
    if (!isFishing) return [...areaNames]

    const tierIndexes = new Set(typeRecords.map((r) => r.tierIndex))
    return SKILL_TIERS.filter(({ index }) => tierIndexes.has(index)).map(({ label }) => label)
  }, [isFishing, typeRecords])

  const itemNames = useMemo(() => {
    const names = new Set<string>()
    for (const r of typeRecords) names.add(isFishing ? r.area : r.name)
    return [...names].sort((a, b) => a.localeCompare(b, 'ja'))
  }, [isFishing, typeRecords])

  useEffect(() => {
    setSelectedArea((current) => {
      if (current === null) return areas[0] ?? null
      // 非釣り: エリア一覧に含まれていれば保持（外部ナビゲーション由来）
      if (!isFishing && areas.includes(current)) return current
      // 釣り: 有効なロケーション名であれば保持
      if (isFishing) {
        const validAreas = new Set(typeRecords.map((r) => r.area))
        if (validAreas.has(current)) return current
      }
      return areas[0] ?? null
    })
    setSelectedItem(null)
  }, [gathering, areas, typeRecords, isFishing, setSelectedArea, setSelectedItem])

  function handleModeChange(next: 'area' | 'item') {
    setMode(next)
    // 別モードへ切り替えたら反対側のフィルターをリセット
    if (next === 'area') setSelectedItem(null)
    else setSelectedArea(null)
  }

  function handleAreaClick(area: string) {
    setSelectedArea(selectedArea === area ? null : area)
    setSelectedItem(null)
  }

  function handleItemClick(name: string) {
    if (isFishing) {
      setSelectedArea(selectedArea === name ? null : name)
      setSelectedItem(null)
      return
    }

    setSelectedItem(selectedItem === name ? null : name)
    setSelectedArea(null)
  }

  return (
    <Box
      sx={{
        gridArea: 'sidebar',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <ButtonGroup size="small" fullWidth>
          <Button
            variant={mode === 'area' ? 'contained' : 'outlined'}
            color={mode === 'area' ? 'warning' : 'inherit'}
            onClick={() => handleModeChange('area')}
            sx={{ fontSize: '0.75rem' }}
          >
            {isFishing ? 'スキル' : '場所'}
          </Button>
          <Button
            variant={mode === 'item' ? 'contained' : 'outlined'}
            color={mode === 'item' ? 'warning' : 'inherit'}
            onClick={() => handleModeChange('item')}
            sx={{ fontSize: '0.75rem' }}
          >
            {isFishing ? 'ロケーション' : 'アイテム'}
          </Button>
        </ButtonGroup>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
        {mode === 'area' ? (
          <Stack direction="column" spacing={0.5}>
            {areas.map((area) => {
              const isSelected = selectedArea === area
              const tier = isFishing ? SKILL_TIERS.find(({ label }) => label === area) : null
              return (
                <Button
                  key={area}
                  size="small"
                  variant={isSelected ? 'contained' : 'text'}
                  color={isSelected ? 'warning' : 'inherit'}
                  onClick={() => handleAreaClick(area)}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    fontWeight: isSelected ? 700 : 400,
                    fontSize: '0.8rem',
                    px: 1,
                  }}
                >
                  {tier ? (
                    <>
                      {tier.label}
                      <span style={{ marginLeft: 4, fontSize: '0.7rem', opacity: 0.7 }}>
                        ({tier.range})
                      </span>
                    </>
                  ) : (
                    area
                  )}
                </Button>
              )
            })}
          </Stack>
        ) : (
          <Stack direction="column" spacing={0.5}>
            {itemNames.map((name) => {
              const isSelected = isFishing ? selectedArea === name : selectedItem === name
              return (
                <Button
                  key={name}
                  size="small"
                  variant={isSelected ? 'contained' : 'text'}
                  color={isSelected ? 'warning' : 'inherit'}
                  onClick={() => handleItemClick(name)}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    fontWeight: isSelected ? 700 : 400,
                    fontSize: '0.8rem',
                    px: 1,
                  }}
                >
                  {name}
                </Button>
              )
            })}
          </Stack>
        )}
      </Box>
    </Box>
  )
}
